/**
 * PlanContext — Fitxero Subscription & Trial System
 *
 * Plan tiers:
 *   trial   → 7 days, UNLIMITED members (full Pro experience)
 *   starter → $25/mo, 200 members, 5 trainers
 *   pro     → $45/mo, unlimited
 *
 * The plan is derived from gym.plan (persisted by the backend).
 * If gym.plan is missing we fall back to "trial".
 * Trial expiry is computed from gym.created_at.
 */
import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from './AuthContext';

// ─── Plan definitions ─────────────────────────────────────────────────────
export const PLANS = {
  trial: {
    id: 'trial',
    name: 'Free Trial',
    memberLimit: Infinity, // Trial = full Pro experience
    trainerLimit: Infinity,
    trialDays: 7,
    features: {
      dashboard: true,
      members: true,
      membershipPlans: true,
      payments: true,
      trainers: true,
      attendance: true,
      announcements: true,
      basicReports: true,
      exportReports: true, // Trial gets everything
      automationReminders: true,
      advancedAnalytics: true, // Trial gets full dashboard
      progressTracking: true,
      revenueInsights: true,
      whatsappReminders: true,
    },
  },
  starter: {
    id: 'starter',
    name: 'Growth', // Renamed to match India market tier
    price: 999, // ₹999/month
    currency: '₹',
    memberLimit: 150, // Up to 150 members
    trainerLimit: 5,
    features: {
      dashboard: true,
      members: true,
      membershipPlans: true,
      payments: true,
      trainers: true,
      attendance: true,
      announcements: true,
      basicReports: true,
      exportReports: false,
      automationReminders: false,
      advancedAnalytics: false, // Advanced charts locked
      progressTracking: false, // Progress tracking locked
      revenueInsights: false, // Revenue insights locked
      whatsappReminders: true, // Growth gets WhatsApp reminders
    },
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    price: 1999, // ₹1,999/month
    currency: '₹',
    memberLimit: Infinity,
    trainerLimit: Infinity,
    features: {
      dashboard: true,
      members: true,
      membershipPlans: true,
      payments: true,
      trainers: true,
      attendance: true,
      announcements: true,
      basicReports: true,
      exportReports: true,
      automationReminders: true,
      advancedAnalytics: true,
      progressTracking: true,
      revenueInsights: true,
      whatsappReminders: true,
    },
  },
};

// ─── Context ──────────────────────────────────────────────────────────────
const PlanContext = createContext(null);

export const usePlan = () => {
  const ctx = useContext(PlanContext);
  if (!ctx) {
    throw new Error('usePlan must be inside PlanProvider');
  }
  return ctx;
};

// ─── Provider ─────────────────────────────────────────────────────────────
export const PlanProvider = ({ children }) => {
  const { gym } = useAuth();

  const value = useMemo(() => {
    if (!gym) {
      return {
        planId: 'trial',
        plan: PLANS.trial,
        isTrial: true,
        trialDaysLeft: 7,
        isExpired: false,
        memberLimit: Infinity,
        trainerLimit: Infinity,
        can: () => true, // No gym data = show everything (loading state)
        nearMemberLimit: false,
        nearTrainerLimit: false,
        atMemberLimit: false,
        memberCount: 0,
        trainerCount: 0,
      };
    }

    // Determine plan — backend stores gym.plan or gym.subscription_plan
    // Backend may store 'paid' for upgraded gyms — map it to 'pro'
    let rawPlan = gym.plan || gym.subscription_plan || 'trial';
    if (rawPlan === 'paid') {
      rawPlan = 'pro';
    } // backward-compat alias
    const planId = PLANS[rawPlan] ? rawPlan : 'trial';
    const plan = PLANS[planId];
    const isTrial = planId === 'trial';

    // Trial expiry
    let trialDaysLeft = 0;
    let trialHoursLeft = 0;
    let trialMinutesLeft = 0;
    let isExpired = false;
    let trialExpiresAt = null;

    if (gym.planStatus === 'EXPIRED') {
      isExpired = true;
      trialDaysLeft = 0;
    } else if (isTrial) {
      // Primary: use created_at + 7 days. Fallback: use trialEndDate or trial_end_date from backend.
      let expiry;
      if (gym.trialEndDate) {
        expiry = new Date(gym.trialEndDate + 'T23:59:59Z');
      } else if (gym.trial_end_date) {
        // Older accounts may not have created_at — use trial_end_date directly
        expiry = new Date(gym.trial_end_date + 'T23:59:59Z');
      } else if (gym.created_at) {
        const created = new Date(gym.created_at);
        expiry = new Date(created.getTime() + plan.trialDays * 86400000);
      } else {
        // Last resort: 7 days from now (never expires visually)
        expiry = new Date(Date.now() + 7 * 86400000);
      }
      trialExpiresAt = expiry;
      const now = new Date();
      const msLeft = Math.max(0, expiry - now);
      trialDaysLeft = Math.floor(msLeft / 86400000);
      trialHoursLeft = Math.floor((msLeft % 86400000) / 3600000);
      trialMinutesLeft = Math.floor((msLeft % 3600000) / 60000);
      isExpired = msLeft === 0 && now > expiry;
    }

    const memberLimit = plan.memberLimit;
    const trainerLimit = plan.trainerLimit;
    const memberCount = gym.member_count ?? 0;
    const trainerCount = gym.trainer_count ?? 0;

    return {
      planId,
      plan,
      isTrial,
      trialDaysLeft,
      trialHoursLeft,
      trialMinutesLeft,
      trialExpiresAt,
      isExpired,
      memberLimit,
      trainerLimit,
      memberCount,
      trainerCount,
      can: feature => plan.features[feature] ?? false,
      atMemberLimit: memberLimit !== Infinity && memberCount >= memberLimit,
      nearMemberLimit: memberLimit !== Infinity && memberCount >= memberLimit * 0.85,
      atTrainerLimit: trainerLimit !== Infinity && trainerCount >= trainerLimit,
      nearTrainerLimit: trainerLimit !== Infinity && trainerCount >= trainerLimit * 0.85,
      isTrialEndingSoon: isTrial && trialDaysLeft <= 2 && !isExpired,
    };
  }, [gym]);

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
};
